<script lang="ts">
	import { format } from 'svelte-i18n';
	import { tick, onMount, createEventDispatcher } from 'svelte';
	import {
		Button,
		Modal,
		Text,
		Input,
		Token,
		toast,
		SelectCard,
		Checkbox,
		Popover,
		Icon,
	} from '@Shelly/shelly-design-system';
	import type { EnergyLocation } from 'src/types/EnergyTariff';
	import { energyPriceApi, type EnergyLocationData } from '../../api/verivoxApi';
	import { energyPriceColorSchemes, type ColorSchemeKey } from '../../utils/colorSchemes';
	import VerivoxTariffsModal from '../tariffs/VerivoxTariffsModal.svelte';
	import { capFirstChar } from 'src/helpers/utilities';
	import TransitionWrapper from 'src/components/Utility/TransitionWrapper.svelte';
	import CacheStatisticRequest from 'src/helpers/statistics/request/chart_cache_statistics_helpers';
	import type {
		GetStatsPeriodRequest,
		GetStatsResponseData,
		RequestStatisticData,
	} from 'src/types/requests/charts/get_stats_period';
	import { dateFormat } from 'src/helpers/date_helper';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { createFunnel } from 'src/helpers/funnel_tracker';
	import { VERIVOX_SETUP_BANNER_ID } from 'src/helpers/banner_views/banner-ids';
	import { markBannerConverted } from 'src/helpers/banner_views/banner-views-helpers';

	const dispatch = createEventDispatcher();
	const verivoxFunnel = createFunnel('verivox_setup');

	export let showModal: boolean = false;
	export let editMode: boolean = false;
	export let locationData: EnergyLocationData | null = null;

	$: postalCode = editMode ? locationData?.postalCode || '' : '';
	let postalCodeError = '';

	$: selectedLocationId = editMode ? locationData?.locationId || null : null;

	let locations: EnergyLocation[] = [];
	let isLoadingLocations = false;

	let isLoadingTariffs = false;
	let cityName = '';
	let showTariffsModal = false;

	let savedPostalCode = '';
	let savedLocationId: string | null = null;
	let savedTotalUsage: number = 2500;

	let locationsSection: HTMLElement;
	let consumptionSection: HTMLElement;
	let customConsumptionInput: HTMLElement;

	let showConsumptionStep = false;
	let isLoadingConsumption = false;
	let lastYearConsumption: number | null = null;
	let hasFullYearData = false;
	let selectedConsumption: number | null = null;
	let consumptionPreference: 'actual' | 'average' | 'custom' | null = null;
	let customConsumption: string = '';

	let consumptionError = '';

	let showConfirmationStep = false;
	let dataConfirmationChecked = false;
	let confirmationSection: HTMLElement;

	const resetForm = () => {
		postalCode = '';
		postalCodeError = '';
		locations = [];
		selectedLocationId = null;
		cityName = '';
		isLoadingLocations = false;
		isLoadingTariffs = false;
		showConsumptionStep = false;
		isLoadingConsumption = false;
		lastYearConsumption = null;
		hasFullYearData = false;
		selectedConsumption = null;
		consumptionPreference = null;
		consumptionError = '';
		savedTotalUsage = 2500;
		showConfirmationStep = false;
		dataConfirmationChecked = false;
	};

	const onClose = () => {
		// Track abandonment if setup wasn't completed
		if (!dataConfirmationChecked) {
			let abandonedStep = 'postal_code';
			if (showConfirmationStep) abandonedStep = 'confirmation';
			else if (showConsumptionStep) abandonedStep = 'consumption';
			else if (selectedLocationId) abandonedStep = 'location';
			verivoxFunnel.trackAbandon(abandonedStep);
		}
		showModal = false;
		resetForm();
	};

	const onSave = async () => {
		if (!postalCode || !selectedLocationId || !validatePostalCode()) {
			toast.open({
				type: 'danger',
				title: $format('error_postal_code_required', {
					default: 'Postleitzahl ist erforderlich',
					locale: 'de',
				}),
			});
			return;
		}
		const selectedLocation = locations.find((loc) => loc.id === selectedLocationId);
		const locationString = selectedLocation?.fullName || '';
		await energyPriceApi.saveLocation(
			postalCode,
			selectedLocationId,
			locationString,
			selectedConsumption || savedTotalUsage,
			consumptionPreference || 'average',
		);
		void markBannerConverted(VERIVOX_SETUP_BANNER_ID, { scope: 'per-surface' });
		dispatch('finishSetup', {
			locationData: {
				postalCode: postalCode,
				locationId: selectedLocationId,
				locationString: cityName,
			},
		});

		onClose();
	};

	const onClear = async () => {
		await energyPriceApi.clearCachedLocation();
		dispatch('finishSetup', {
			locationData: {
				postalCode: '',
				locationId: '',
				locationString: '',
			},
		});

		resetForm();
		onClose();
	};

	const scrollToElement = async (element: HTMLElement | undefined) => {
		await tick();
		if (element) {
			element.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	};

	const validatePostalCode = (): boolean => {
		if (!postalCode || postalCode.trim().length === 0) {
			postalCodeError = $format('error_postal_code_required', {
				default: 'Postleitzahl ist erforderlich',
				locale: 'de',
			});
			return false;
		}

		// Remove any whitespace for validation
		const trimmedPostalCode = postalCode.trim();

		// Check if it contains only digits
		if (!/^\d+$/.test(trimmedPostalCode)) {
			postalCodeError = $format('error_postal_code_invalid_format', {
				default: 'Postleitzahl darf nur Zahlen enthalten',
				locale: 'de',
			});
			return false;
		}

		// Check if it's exactly 5 digits
		if (trimmedPostalCode.length !== 5) {
			postalCodeError = $format('error_postal_code_length', {
				default: 'Postleitzahl muss genau 5 Ziffern lang sein',
				locale: 'de',
			});
			return false;
		}

		postalCodeError = '';
		return true;
	};

	const sanitizePostalCode = (value: string): string => {
		// Remove all non-digit characters and limit to 5 digits
		return value.replace(/\D/g, '').slice(0, 5);
	};

	// Sanitize postal code on change (only if it contains non-digits or exceeds 5 chars)
	$: if (postalCode !== undefined && postalCode !== null && typeof postalCode === 'string') {
		const sanitized = sanitizePostalCode(postalCode);
		if (sanitized !== postalCode) {
			postalCode = sanitized;
		}
		// Clear error when user starts typing valid characters
		if (sanitized.length > 0 && postalCodeError) {
			postalCodeError = '';
		}
	}

	const fetchLocations = async () => {
		if (!validatePostalCode()) return;

		isLoadingLocations = true;
		postalCodeError = '';
		locations = [];
		selectedLocationId = null;

		try {
			const res = await energyPriceApi.fetchLocations(postalCode);

			if (res?.isok && res?.data) {
				cityName = res.data.mainName || '';
				locations = res.data.locations || [];
				verivoxFunnel.trackStep('postal_code');

				if (locations.length === 0) {
					selectedLocationId = '0';
					savedPostalCode = postalCode.trim();
					savedLocationId = '0';
					// Save location
					await energyPriceApi.saveLocation(savedPostalCode, savedLocationId, '');
					verivoxFunnel.trackStep('location');
					// Show consumption selection step
					showConsumptionStep = true;
					await scrollToElement(consumptionSection);
					await fetchLastYearConsumption();
				} else if (locations.length === 1) {
					selectedLocationId = locations[0].id;
					savedPostalCode = postalCode.trim();
					savedLocationId = locations[0].id;
					// Save location
					await energyPriceApi.saveLocation(
						savedPostalCode,
						savedLocationId,
						locations[0].fullName,
					);
					verivoxFunnel.trackStep('location');
					// Show consumption selection step
					showConsumptionStep = true;
					await scrollToElement(consumptionSection);
					await fetchLastYearConsumption();
				} else {
					await scrollToElement(locationsSection);
					// In edit mode, if we already have a selected location, show consumption step
					if (editMode && selectedLocationId) {
						showConsumptionStep = true;
						await tick();
						await scrollToElement(consumptionSection);
						await fetchLastYearConsumption();
					}
				}
			} else {
				postalCodeError = res?.error
					? res.error
					: $format('error_fetching_locations', {
							default:
								'Standorte konnten nicht abgerufen werden. Bitte versuchen Sie es erneut.',
							locale: 'de',
						});
			}
		} catch (error) {
			console.error('Error fetching locations:', error);
			postalCodeError = $format('error_fetching_locations', {
				default: 'Standorte konnten nicht abgerufen werden. Bitte versuchen Sie es erneut.',
				locale: 'de',
			});
			locations = [];
		} finally {
			isLoadingLocations = false;
		}
	};

	const getMonthConsumption = async (monthOffset: number): Promise<number> => {
		const now = new Date();
		const targetDate = new Date(now.getFullYear(), now.getMonth() - monthOffset, 1);
		targetDate.setHours(0, 0, 0, 0);

		const lastDayOfMonth = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0);
		lastDayOfMonth.setHours(23, 59, 59, 999);

		const reqData: RequestStatisticData = {
			date_range: 'custom',
			date_from: dateFormat(targetDate, 'Y-m-d H:i:s'),
			date_to: dateFormat(lastDayOfMonth, 'Y-m-d H:i:s'),
		};

		try {
			const res: GetStatsPeriodRequest = await CacheStatisticRequest.getData(
				'v2/statistics/power-consumption/overall',
				reqData,
				undefined,
			);

			if (!res?.error && res?.history) {
				const resData: GetStatsResponseData = res?.history
					? (res as GetStatsResponseData)
					: res.data;
				let total = 0;

				if (resData.history && Array.isArray(resData.history)) {
					resData.history.forEach((item: any) => {
						if (item.consumption) {
							total += item.consumption;
						}
					});
				}

				return total / 1000;
			}
		} catch (err) {
			console.error('Failed to fetch consumption data:', err);
		}

		return 0;
	};

	const fetchLastYearConsumption = async () => {
		isLoadingConsumption = true;
		consumptionError = '';
		lastYearConsumption = null;
		hasFullYearData = false;

		try {
			// Fetch consumption for the previous 12 months (rolling year)
			const monthlyConsumptions: number[] = [];

			// Fetch data for each of the last 12 months
			for (let monthOffset = 1; monthOffset <= 12; monthOffset++) {
				const consumption = await getMonthConsumption(monthOffset);
				monthlyConsumptions.push(consumption);
			}

			// Verify we have data for all 12 months (each month must have consumption > 0)
			const hasDataForAllMonths =
				monthlyConsumptions.length === 12 &&
				monthlyConsumptions.every((consumption) => consumption > 0);

			if (hasDataForAllMonths) {
				// Calculate total annual consumption from the 12 months
				const totalConsumption = monthlyConsumptions.reduce(
					(sum, consumption) => sum + consumption,
					0,
				);
				lastYearConsumption = Math.round(totalConsumption * 10) / 10;
				selectedConsumption = Math.round(totalConsumption * 10) / 10;
				hasFullYearData = true;
				// Always default to predefined average when preference is not set yet
				if (consumptionPreference === null) {
					consumptionPreference = 'average';
					selectedConsumption = 2500;
				}
			} else {
				consumptionError = $format('energy_price_no_full_year_consumption_data', {
					default: 'Vollständige Jahresverbrauchsdaten nicht verfügbar',
					locale: 'de',
				});
				selectedConsumption = 2500;
				hasFullYearData = false;
				// Default to average if no full year data
				if (consumptionPreference === null) {
					consumptionPreference = 'average';
				}
			}
		} catch (err) {
			console.error('Error fetching consumption:', err);
			consumptionError = $format('error_fetching_consumption', {
				default: 'Verbrauchsdaten konnten nicht abgerufen werden',
				locale: 'de',
			});
			selectedConsumption = 2500;
			hasFullYearData = false;
			// Default to average on error
			if (consumptionPreference === null) {
				consumptionPreference = 'average';
			}
		} finally {
			isLoadingConsumption = false;
		}
	};

	const selectLocation = async (locationId: string) => {
		savedPostalCode = postalCode.trim();
		savedLocationId = locationId;
		selectedLocationId = locationId;

		// Find the location object to get the fullName
		const selectedLocation = locations.find((loc) => loc.id === locationId);
		const locationString = selectedLocation?.fullName || '';

		// Save location (without consumption yet)
		await energyPriceApi.saveLocation(savedPostalCode, savedLocationId, locationString);
		verivoxFunnel.trackStep('location');

		// Show consumption selection step
		showConsumptionStep = true;
		await scrollToElement(consumptionSection);
		await fetchLastYearConsumption();
	};

	const selectLocationToken = async (locationId: string) => {
		selectedLocationId = locationId;
		if (!editMode) {
			await selectLocation(locationId);
		} else {
			// In edit mode, show consumption step when location is selected
			savedPostalCode = postalCode.trim();
			// savedLocationId = locationId;
			// const selectedLocation = locations.find((loc) => loc.id === locationId);
			// const locationString = selectedLocation?.fullName || '';
			// await energyPriceApi.saveLocation(savedPostalCode, savedLocationId, locationString);
			showConsumptionStep = true;
			await tick();
			await scrollToElement(consumptionSection);
			await fetchLastYearConsumption();
		}
	};

	const useLastYearConsumption = async () => {
		if (lastYearConsumption !== null && hasFullYearData) {
			selectedConsumption = Math.round(lastYearConsumption * 10) / 10;
			savedTotalUsage = selectedConsumption;
			consumptionPreference = 'actual';
			// Save consumption preference immediately
			if (savedPostalCode && savedLocationId) {
				const selectedLocation = locations.find((loc) => loc.id === savedLocationId);
				const locationString = selectedLocation?.fullName || cityName;
				// await energyPriceApi.saveLocation(
				// 	savedPostalCode,
				// 	savedLocationId,
				// 	locationString,
				// 	savedTotalUsage,
				// 	consumptionPreference,
				// );
			}
		}
	};

	const useAverageConsumption = async () => {
		selectedConsumption = 2500;
		savedTotalUsage = selectedConsumption;
		consumptionPreference = 'average';
		// Save consumption preference immediately
		if (savedPostalCode && savedLocationId) {
			const selectedLocation = locations.find((loc) => loc.id === savedLocationId);
			const locationString = selectedLocation?.fullName || cityName;
			// await energyPriceApi.saveLocation(
			// 	savedPostalCode,
			// 	savedLocationId,
			// 	locationString,
			// 	savedTotalUsage,
			// 	consumptionPreference,
			// );
		}
	};

	const useCustomConsumption = async () => {
		selectedConsumption = parseFloat(customConsumption);
		savedTotalUsage = selectedConsumption;
		consumptionPreference = 'custom';

		await tick();
		await scrollToElement(customConsumptionInput);
	};

	type ConsumptionOptionId = 'average' | 'custom' | 'actual';

	type ConsumptionOption = {
		id: ConsumptionOptionId;
		title: string;
		descriptiveText?: string;
		info: string;
		disabled?: boolean;
		onSelect: () => void | Promise<void>;
	};

	$: consumptionOptions = ((): ConsumptionOption[] => {
		const average: ConsumptionOption = {
			id: 'average',
			title: $format('energy_price_use_average_consumption', {
				default: 'Durchschnittsverbrauch verwenden',
				locale: 'de',
			}),
			descriptiveText: $format('energy_price_average_consumption_value', {
				default: '2.500 kWh pro Jahr',
				locale: 'de',
			}),
			info: $format('energy_price_average_consumption_source', {
				default:
					'Durchschnittlicher Jahresverbrauch eines deutschen Haushalts (~2.500 kWh), verwendet für den Tarifvergleich',
				locale: 'de',
			}),
			onSelect: useAverageConsumption,
		};

		const custom: ConsumptionOption = {
			id: 'custom',
			title: $format('energy_price_use_custom_consumption', {
				default: 'Einen anderen Verbrauchswert eingeben',
				locale: 'de',
			}),
			descriptiveText: customConsumption
				? $format('energy_price_custom_consumption_value', {
						default: '{consumption} kWh pro Jahr',
						values: { consumption: customConsumption },
						locale: 'de',
					})
				: undefined,
			info: $format('energy_price_custom_consumption_source', {
				default: 'Vom Nutzer eingegebener jährlicher Verbrauchswert',
				locale: 'de',
			}),
			onSelect: useCustomConsumption,
		};

		const options: ConsumptionOption[] = [];

		if (!consumptionError && lastYearConsumption !== null) {
			options.push({
				id: 'actual',
				title: $format('energy_price_use_shelly_consumption', {
					default: 'Meinen Shelly-Verbrauch verwenden',
					locale: 'de',
				}),
				descriptiveText: $format('energy_price_shelly_consumption_value', {
					default: 'Letzte 12 Monate: {consumption} kWh',
					values: {
						consumption: lastYearConsumption.toFixed(1),
					},
					locale: 'de',
				}),
				info: $format('energy_price_shelly_consumption_source', {
					default:
						'Berechnet aus Shelly-Energieverbrauchsdaten (rollierende 12-Monats-Summe)',
					locale: 'de',
				}),
				disabled: !hasFullYearData,
				onSelect: useLastYearConsumption,
			});
		}

		options.push(average, custom);

		const selected = options.find((option) => option.id === consumptionPreference);
		const rest = options.filter((option) => option.id !== consumptionPreference);
		return selected ? [selected, ...rest] : options;
	})();

	const proceedWithConsumption = async () => {
		if (consumptionPreference === null) {
			toast.open({
				type: 'danger',
				title: $format('error_consumption_required', {
					default: 'Bitte wählen Sie eine Verbrauchsoption',
					locale: 'de',
				}),
			});
			return;
		}

		// Calculate consumption based on preference
		if (consumptionPreference === 'actual' && lastYearConsumption !== null && hasFullYearData) {
			savedTotalUsage = Math.round(lastYearConsumption * 10) / 10;
		} else if (consumptionPreference === 'custom') {
			savedTotalUsage = Math.round(parseFloat(customConsumption) * 10) / 10;
		} else {
			savedTotalUsage = 2500;
		}

		verivoxFunnel.trackStep('consumption');

		// Save location with consumption preference
		const selectedLocation = locations.find((loc) => loc.id === savedLocationId);
		const locationString = selectedLocation?.fullName || cityName;
		// await energyPriceApi.saveLocation(
		// 	savedPostalCode,
		// 	savedLocationId || '',
		// 	locationString,
		// 	savedTotalUsage,
		// 	consumptionPreference || null,
		// );

		// Check if data confirmation is already accepted
		const cachedLocation = energyPriceApi.getCachedLocation();
		if (cachedLocation?.dataConfirmationAccepted) {
			// Already confirmed, proceed to tariffs modal
			void markBannerConverted(VERIVOX_SETUP_BANNER_ID, { scope: 'per-surface' });
			dispatch('finishSetup', {
				locationData: {
					postalCode: savedPostalCode,
					locationId: savedLocationId || '',
					locationString: cityName,
				},
			});

			showModal = false;
			await tick();
			showTariffsModal = true;
		} else {
			// Show confirmation step
			showConsumptionStep = false;
			showConfirmationStep = true;
			await scrollToElement(confirmationSection);
		}
	};

	const handleDataConfirmation = async () => {
		if (!dataConfirmationChecked) {
			return;
		}

		verivoxFunnel.trackStep('confirmation');
		verivoxFunnel.trackComplete();

		// Save confirmation status
		const selectedLocation = locations.find((loc) => loc.id === savedLocationId);
		const locationString = selectedLocation?.fullName || cityName;
		await energyPriceApi.saveLocation(
			savedPostalCode,
			savedLocationId || '',
			locationString,
			savedTotalUsage,
			consumptionPreference || null,
			true,
		);

		void markBannerConverted(VERIVOX_SETUP_BANNER_ID, { scope: 'per-surface' });

		dispatch('finishSetup', {
			locationData: {
				postalCode: savedPostalCode,
				locationId: savedLocationId || '',
				locationString: cityName,
			},
		});

		showModal = false;
		await tick();
		showTariffsModal = true;
	};

	const onTariffsModalClose = () => {
		showTariffsModal = false;
		resetForm();
	};

	let currentScheme: ColorSchemeKey = 'scheme1';

	onMount(() => {
		if (editMode) {
			locationData = energyPriceApi.getCachedLocation();
			postalCode = locationData?.postalCode || '';
			selectedLocationId = locationData?.locationId || '';
			savedPostalCode = postalCode;
			savedLocationId = selectedLocationId;
			cityName = locationData?.locationString || '';
			consumptionPreference = locationData?.consumptionPreference || null;
			// Calculate consumption based on preference
			if (consumptionPreference === 'actual') {
				// Will be calculated when consumption data is fetched
				savedTotalUsage = locationData?.annualConsumption || 2500;
				selectedConsumption = savedTotalUsage;
			} else if (consumptionPreference === 'custom') {
				savedTotalUsage = locationData?.annualConsumption || 2500;
				selectedConsumption = savedTotalUsage;
				customConsumption = savedTotalUsage.toString();
			} else {
				// Default to average if preference is 'average' or null
				savedTotalUsage = 2500;
				selectedConsumption = 2500;
			}
			// Show consumption step if we have location data
			if (postalCode && selectedLocationId) {
				showConsumptionStep = true;
				// If preference is 'actual', fetch consumption data
				if (consumptionPreference === 'actual') {
					fetchLastYearConsumption();
				}
			}
			fetchLocations();
		}
		currentScheme = energyPriceColorSchemes.getCurrentScheme();
	});

	$: showLocations = locations.length > 1;
	$: isLoading = isLoadingLocations || isLoadingTariffs;

	$: {
		if (postalCode.length === 0) {
			showLocations = false;
			locations = [];
		}
	}
</script>

{#if showModal}
	<Modal
		{showModal}
		on:close={onClose}
		title={$format('energy_price_setup_prompt_title', {
			default: 'Günstige Stromtarife entdecken',
			locale: 'de',
		})}
	>
		<div slot="content" class="energy-setup-content">
			<!-- Eneter Postal Code  -->
			<TransitionWrapper>
				<section class="mb-l">
					<Text styleType="h2" class="mb-m">
						{$format('energy_price_setup_postal_code_heading', {
							default: 'Geben Sie Ihre Postleitzahl ein',
							locale: 'de',
						})}
					</Text>
					<Text styleType="body" class="mb-m">
						{$format('energy_price_setup_postal_code_description', {
							default: 'Wir suchen nach verfügbaren Stromtarifen in Ihrer Region.',
							locale: 'de',
						})}
					</Text>
					<div class="d-flex align-items-start gap-m">
						<Input
							class="mb-s mb-md-0 width-full width-md-auto"
							id="postalCode"
							aria-label={$format('label_postal_code', {
								default: 'postleitzahl',
								locale: 'de',
							})}
							label={$format('label_postal_code', {
								default: 'Postal Code',
								locale: 'de',
							})}
							bind:value={postalCode}
							error={postalCodeError}
							disabled={isLoading}
							maxlength={5}
							type="text"
							inputmode="numeric"
							pattern="[0-9]{5}"
							on:keydown={(e) => {
								if (e.detail.key === 'Enter') {
									fetchLocations();
								}
							}}
						/>

						<Button
							class="mb-s mb-md-0 width-full width-md-auto"
							variant="primary"
							size="m"
							disabled={isLoading}
							on:click={fetchLocations}
						>
							{capFirstChar($format('button_search', { default: 'Search' }))}
						</Button>
					</div>

					{#if isLoadingLocations}
						<div class="loading-state text-center mt-l">
							<div class="spinner-border text-primary" role="status">
								<span class="visually-hidden"
									>{capFirstChar($format('loading', { default: 'Loading...' }))}
								</span>
							</div>
							<Text class="mt-s" styleType="body-small">
								{capFirstChar(
									$format('energy_price_setup_searching_locations', {
										default: 'Suche nach Standorten...',
										locale: 'de',
									}),
								)}
							</Text>
						</div>
					{/if}
				</section>
			</TransitionWrapper>

			{#if showLocations}
				<section bind:this={locationsSection} class="mb-l">
					<!-- Select Location Heading -->
					<TransitionWrapper>
						<hr class="mb-l" />
						<Text styleType="h2" class="mb-m">
							{$format('energy_price_setup_select_location_heading', {
								default: 'Wählen Sie Ihren Standort',
								locale: 'de',
							})}
						</Text>
						<Text styleType="body" class="mb-m">
							{$format('energy_price_setup_select_location_description', {
								default:
									'Mehrere Standorte in {city} gefunden. Bitte wählen Sie Ihren aus:',
								values: { city: cityName },
								locale: 'de',
							})}
						</Text>
					</TransitionWrapper>

					<!-- Select Location Tokens Section -->
					<TransitionWrapper>
						<div class="d-flex flex-wrap gap-s">
							{#each locations as location}
								<Token
									id="token-location-{location.id}"
									title={location.fullName}
									selected={selectedLocationId === location.id}
									size="m"
									disabled={isLoadingTariffs}
									on:click={() => selectLocationToken(location.id)}
								/>
							{/each}
						</div>
					</TransitionWrapper>
				</section>
			{/if}

			{#if showConsumptionStep}
				<section bind:this={consumptionSection} class="mb-l">
					<hr class="mb-l" />
					<Text styleType="h2" class="mb-m">
						{$format('energy_price_setup_consumption_heading', {
							default: 'Wählen Sie Ihren Energieverbrauch',
							locale: 'de',
						})}
					</Text>
					<Text styleType="body" class="mb-m">
						{$format('energy_price_setup_consumption_description', {
							default:
								'Wählen Sie Ihren jährlichen Energieverbrauch für den Tarifvergleich. Der Durchschnittswert wird empfohlen.',
							locale: 'de',
						})}
					</Text>
					{#if isLoadingConsumption}
						<div class="loading-state text-center mt-l">
							<div class="spinner-border text-primary" role="status">
								<span class="visually-hidden"
									>{capFirstChar($format('loading', { default: 'Loading...' }))}
								</span>
							</div>
							<Text class="mt-s" styleType="body-small">
								{capFirstChar(
									$format('energy_price_setup_fetching_consumption', {
										default: 'Ihre Verbrauchsdaten werden abgerufen...',
										locale: 'de',
									}),
								)}
							</Text>
						</div>
					{:else}
						{#if consumptionError}
							<Text styleType="body-small" class="text-danger mb-m">
								{consumptionError}
							</Text>
						{/if}

						{#each consumptionOptions as option, index (option.id)}
							<SelectCard
								cardBgColor={$showNewLayout ? 'color-1' : undefined}
								class="width-full {index === 0 ? 'mb-m' : 'mb-s'}"
								type="horizontal"
								variant="radio"
								size={index === 0 ? undefined : 's'}
								title={option.title}
								descriptiveText={option.descriptiveText}
								info={option.info}
								selected={consumptionPreference === option.id}
								disabled={option.disabled}
								on:click={option.onSelect}
							/>
							{#if option.id === 'custom' && consumptionPreference === 'custom'}
								<TransitionWrapper>
									<section bind:this={customConsumptionInput} class="mb-s">
										<Input
											class="width-full"
											id="customConsumption"
											aria-label={$format('label_custom_consumption', {
												default: 'Jährlicher Verbrauch (kWh)',
											})}
											label={$format('label_custom_consumption', {
												default: 'Jährlicher Verbrauch (kWh)',
											})}
											type="number"
											placeholder="Verbrauchswert"
											bind:value={customConsumption}
										/>
									</section>
								</TransitionWrapper>
							{/if}
						{/each}
					{/if}
				</section>
			{/if}

			{#if showConfirmationStep}
				<section bind:this={confirmationSection} class="mb-l">
					<hr class="mb-l" />
					<Text styleType="h2" class="mb-m">
						{$format('energy_price_data_confirmation_title', {
							default: 'Datenbestätigung',
							locale: 'de',
						})}
					</Text>
					<Text styleType="body" class="mb-m">
						{$format('energy_price_data_confirmation_description', {
							default:
								'Bitte bestätigen Sie, dass wir Ihre eingegebenen Daten speichern und an Verivox übermitteln dürfen, um Ihnen passende Stromtarife vorzuschlagen.',
							locale: 'de',
						})}
					</Text>
					<Text styleType="body-small" class="mb-l">
						{$format('energy_price_cookie_agreement', {
							default:
								'Verivox verwendet Cookies, um Tarifsuchdienste bereitzustellen. Durch Fortfahren stimmen Sie zu, dass Cookies zu diesem Zweck auf Ihrem Gerät gesetzt werden.',
							locale: 'de',
						})}
					</Text>
					<div class="confirmation-checkbox-wrapper">
						<Checkbox
							id="data-confirmation-checkbox"
							size="m"
							bind:checked={dataConfirmationChecked}
							class="confirmation-checkbox"
							aria-label={$format('energy_price_data_confirmation_checkbox', {
								default:
									'Ich bestätige, dass meine eingegebenen Daten gespeichert und an Verivox übermittelt werden dürfen',
								locale: 'de',
							})}
						>
							<span class="checkbox-label">
								{$format('energy_price_data_confirmation_checkbox', {
									default:
										'Ich bestätige, dass meine eingegebenen Daten gespeichert und an Verivox übermittelt werden dürfen',
									locale: 'de',
								})}
								<Popover type="hover" hideCloseBtn={true} placement="top" fixedWidth>
									<Icon
										icon="fa-solid fa-circle-info"
										variant="help"
										size="s"
										class="info-icon"
										aria-label={$format('energy_price_data_info', {
											default: 'Informationen zu gespeicherten Daten',
											locale: 'de',
										})}
									/>
									<svelte:fragment slot="customContent">
										<Text styleType="body-small">
											{$format('energy_price_data_info_details', {
												default:
													'Folgende Daten werden gespeichert und an Verivox übermittelt, um Ihnen passende Stromtarife vorzuschlagen:\n\n• Postleitzahl: {postalCode}\n• Standort-ID: {locationId}\n• Geschätzter Jahresverbrauch: {totalUsage} kWh\n\nDiese Daten werden verwendet, um:\n• Verfügbare Stromtarife in Ihrer Region zu finden\n• Tarife basierend auf Ihrem Verbrauch zu filtern\n• Ihnen die besten Angebote zu präsentieren\n\nIhre Daten werden sicher gespeichert und nur für die Tarifsuche verwendet.',
												values: {
													postalCode: savedPostalCode || '-',
													locationId: savedLocationId || '-',
													totalUsage: savedTotalUsage.toString(),
												},
												locale: 'de',
											})}
										</Text>
									</svelte:fragment>
								</Popover>
							</span>
						</Checkbox>
					</div>
				</section>
			{/if}
		</div>

		<div slot="footer" class="d-flex justify-content-end">
			{#if editMode}
				<div class="width-full d-flex justify-content-between gap-s">
					<Button class="" variant="danger" size="m" on:click={() => onClear()}>
						{capFirstChar($format('button_clear', { default: 'Clear' }))}
					</Button>
					<div class="d-flex gap-s">
						<Button
							disabled={!postalCode ||
								!selectedLocationId ||
								(consumptionPreference === 'custom' &&
									(!customConsumption ||
										customConsumption.length === 0 ||
										parseFloat(customConsumption) <= 0))}
							class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full mr-s"
							variant="primary"
							size="m"
							on:click={() => onSave()}
						>
							{capFirstChar(
								$format('button_save', { default: 'Speichern', locale: 'de' }),
							)}
						</Button>
						<Button
							size="m"
							class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full "
							variant="secondary"
							on:click={() => onClose()}
						>
							{capFirstChar(
								$format('button_close', { default: 'Schließen', locale: 'de' }),
							)}
						</Button>
					</div>
				</div>
			{:else if showConfirmationStep}
				<div class="width-full d-flex justify-content-end gap-s">
					<Button
						class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full "
						variant="secondary"
						on:click={() => {
							showConfirmationStep = false;
							showConsumptionStep = true;
							dataConfirmationChecked = false;
						}}
					>
						{capFirstChar($format('button_back', { default: 'Zurück', locale: 'de' }))}
					</Button>
					<Button
						class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full "
						variant="primary"
						disabled={!dataConfirmationChecked}
						on:click={handleDataConfirmation}
					>
						{capFirstChar($format('button_continue', { default: 'Weiter', locale: 'de' }))}
					</Button>
				</div>
			{:else if showConsumptionStep}
				<div class="width-full d-flex justify-content-end gap-s">
					<Button
						class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full "
						variant="secondary"
						on:click={() => {
							showConsumptionStep = false;
							consumptionPreference = null;
						}}
					>
						{capFirstChar($format('button_back', { default: 'Zurück', locale: 'de' }))}
					</Button>
					<Button
						class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full "
						variant="primary"
						disabled={consumptionPreference === null ||
							isLoadingConsumption ||
							(consumptionPreference === 'custom' &&
								(!customConsumption ||
									customConsumption.length === 0 ||
									parseFloat(customConsumption) <= 0))}
						on:click={proceedWithConsumption}
					>
						{capFirstChar($format('button_continue', { default: 'Weiter', locale: 'de' }))}
					</Button>
				</div>
			{:else}
				<Button
					class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full "
					variant="secondary"
					on:click={() => onClose()}
				>
					{capFirstChar($format('button_close', { default: 'Close' }))}
				</Button>
			{/if}
		</div>
	</Modal>
{/if}

<!-- Secondary Modal for Energy Price Options -->
<VerivoxTariffsModal
	bind:showModal={showTariffsModal}
	on:close={onTariffsModalClose}
	postalCode={savedPostalCode}
	locationId={savedLocationId}
	totalUsage={savedTotalUsage}
/>

<style>
	.energy-setup-content {
		max-height: 60vh;
		overflow-y: auto;
		padding-right: var(--spacing-s);
	}

	.loading-state {
		padding: var(--spacing-l);
	}

	.gap-m {
		gap: var(--spacing-m);
	}

	.gap-s {
		gap: var(--spacing-s);
	}

	/* .energy-price-tariffs-wrapper {
		border-radius: 12px;
		padding: 20px;
		margin-top: 8px;
		min-height: 200px;
		position: relative;
		overflow: hidden;
	} */

	/* .energy-price-tariffs-wrapper::before {
		content: '';
		position: absolute;
		top: -50%;
		right: -10%;
		width: 200px;
		height: 200px;
		background: rgba(255, 255, 255, 0.1);
		border-radius: 50%;
		pointer-events: none;
	} */

	:global(.energy-price-tariffs-wrapper .price-options) {
		position: relative;
		z-index: 1;
	}

	.confirmation-checkbox-wrapper {
		display: flex;
		align-items: flex-start;
		margin-top: var(--spacing-l);
	}

	.confirmation-checkbox {
		width: 100%;
	}

	.checkbox-label {
		display: inline-flex;
		align-items: center;
		gap: var(--spacing-xs);
	}

	.info-icon {
		cursor: help;
		flex-shrink: 0;
	}
</style>
